'use client';

import { Box, Button } from '@mui/material';

import { useT } from '@/hooks';
import type { EventMarket, EventOutcome, Market, Outcome } from '@/types';
import { formatProbability } from '@/utils';

interface MobileActionButtonsProps {
  market: Market | EventMarket | undefined;
  onTrade: (outcome: Outcome | EventOutcome, isBuyOrder: boolean) => void;
}

export function MobileActionButtons({ market, onTrade }: MobileActionButtonsProps) {
  const { t } = useT();
  let outcomes: any[] = [];
  if (market && Array.isArray(market.outcomes)) {
    outcomes = market.outcomes;
  } else if (market && typeof market.outcomes === 'string') {
    try {
      outcomes = JSON.parse(market.outcomes);
    } catch (e) {
      outcomes = [];
    }
  }

  if (!outcomes || outcomes.length !== 2) return null;

  const yesOutcome = outcomes.find((o) => o?.name?.toLowerCase() === 'yes') || outcomes[0];
  const noOutcome = outcomes.find((o) => o?.name?.toLowerCase() === 'no') || outcomes[1];

  if (!yesOutcome || !noOutcome) return null;

  const pYes = Number(yesOutcome.price) || 0;
  const yesPrice = formatProbability(pYes, 0);

  const pNo = Number(noOutcome.price) || 0;
  const noPrice = formatProbability(pNo, 0);

  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        bottom: 64, // Account for bottom navigation bar (64px height)
        left: 0,
        right: 0,
        gap: 1,
        p: 1.5,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 999, // Below bottom nav (1000) but above content
      }}
    >
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() => onTrade(yesOutcome, true)}
        sx={{
          bgcolor: '#22c55e',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '1rem',
          py: 1.5,
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#16a34a',
            boxShadow: 'none',
          },
        }}
      >
        {t('howItWorks.buyYes')} {yesPrice}¢
      </Button>
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() => onTrade(noOutcome, true)}
        sx={{
          bgcolor: '#ef4444',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '1rem',
          py: 1.5,
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#dc2626',
            boxShadow: 'none',
          },
        }}
      >
        {t('howItWorks.buyNo')} {noPrice}¢
      </Button>
    </Box>
  );
}
